import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { 
  Clock, 
  Calendar, 
  Settings, 
  X,
  Plus,
  Save
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const AdminSlotManagement = () => {
  const { toast } = useToast();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    opening_time: '07:00',
    closing_time: '16:00',
    slot_capacity: 6,
    slot_duration: 30,
    working_days: [1, 2, 3, 4, 5],
    cutoff_hours: 72,
    daily_summary_skip_weekends: true,
    daily_summary_skip_holidays: true,
    lunch_break_enabled: true,
    lunch_start: '11:00',
    lunch_end: '11:30',
    lunch_buffer_before_minutes: 30
  });
  
  const [closedDates, setClosedDates] = useState([]);
  const [newClosedStart, setNewClosedStart] = useState('');
  const [newClosedEnd, setNewClosedEnd] = useState('');
  const [newClosedReason, setNewClosedReason] = useState('');

  // Group individual closed dates into consecutive periods (same reason)
  const groupIntoPeriods = (items) => {
    const sorted = [...items].sort((a, b) => a.date.localeCompare(b.date));
    const periods = [];
    for (const item of sorted) {
      const last = periods[periods.length - 1];
      if (last) {
        const next = new Date(`${last.end}T00:00:00`);
        next.setDate(next.getDate() + 1);
        const y = next.getFullYear();
        const m = String(next.getMonth() + 1).padStart(2, '0');
        const d = String(next.getDate()).padStart(2, '0');
        const nextStr = `${y}-${m}-${d}`;
        if (item.date === nextStr && (item.reason || '') === (last.reason || '')) {
          last.end = item.date;
          last.days += 1;
          continue;
        }
      }
      periods.push({ start: item.date, end: item.date, reason: item.reason || '', days: 1 });
    }
    return periods;
  };

  const formatNo = (dateStr) =>
    new Date(`${dateStr}T00:00:00`).toLocaleDateString('no-NO', { day: 'numeric', month: 'short', year: 'numeric' });

  const closedPeriods = groupIntoPeriods(closedDates);

  const weekDays = [
    { id: 1, name: 'Mandag' },
    { id: 2, name: 'Tirsdag' },
    { id: 3, name: 'Onsdag' },
    { id: 4, name: 'Torsdag' },
    { id: 5, name: 'Fredag' },
    { id: 6, name: 'Lørdag' },
    { id: 7, name: 'Søndag' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, closedDatesRes] = await Promise.all([
        axios.get(`${API}/slots/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/slots/closed-dates`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setSettings(settingsRes.data);
      setClosedDates(closedDatesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke laste data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/slots/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Innstillinger Lagret! ✅",
        description: "Åpningstider og kapasitet er oppdatert"
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke lagre innstillinger",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleWorkingDay = (dayId) => {
    setSettings(prev => ({
      ...prev,
      working_days: prev.working_days.includes(dayId)
        ? prev.working_days.filter(d => d !== dayId)
        : [...prev.working_days, dayId].sort()
    }));
  };

  const handleAddClosedPeriod = async () => {
    if (!newClosedStart) {
      toast({
        title: "Mangler startdato",
        description: "Vennligst velg en startdato",
        variant: "destructive"
      });
      return;
    }

    const end = newClosedEnd || newClosedStart;
    if (end < newClosedStart) {
      toast({
        title: "Ugyldig periode",
        description: "Sluttdato kan ikke være før startdato",
        variant: "destructive"
      });
      return;
    }

    try {
      const res = await axios.post(`${API}/slots/closed-dates/range`, null, {
        params: { start: newClosedStart, end, reason: newClosedReason },
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Periode stengt! ✅",
        description: `${res.data.added} dag(er) er nå stengt`
      });

      setNewClosedStart('');
      setNewClosedEnd('');
      setNewClosedReason('');
      fetchData();
    } catch (error) {
      console.error('Error adding closed period:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke legge til stengt periode",
        variant: "destructive"
      });
    }
  };

  const handleRemovePeriod = async (start, end) => {
    try {
      await axios.delete(`${API}/slots/closed-dates/range`, {
        params: { start, end },
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Periode åpnet! ✅",
        description: `Perioden er nå åpen igjen`
      });

      fetchData();
    } catch (error) {
      console.error('Error removing period:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke åpne perioden",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white text-xl">Laster innstillinger...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-white mb-2">
          <span className="bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text text-transparent">
            Slot Management
          </span>
        </h2>
        <p className="text-gray-400">Administrer åpningstider og kapasitet</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Opening Hours & Capacity */}
        <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center space-x-2">
              <Clock className="w-5 h-5 text-red-400" />
              <span>Åpningstider & Kapasitet</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Åpningstid</Label>
                <Input
                  type="time"
                  value={settings.opening_time}
                  onChange={(e) => setSettings(prev => ({ ...prev, opening_time: e.target.value }))}
                  className="bg-gray-900 border-gray-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Stengetid</Label>
                <Input
                  type="time"
                  value={settings.closing_time}
                  onChange={(e) => setSettings(prev => ({ ...prev, closing_time: e.target.value }))}
                  className="bg-gray-900 border-gray-700 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Kapasitet (antall plasser)</Label>
              <Input
                type="number"
                min="1"
                max="20"
                value={settings.slot_capacity}
                onChange={(e) => setSettings(prev => ({ ...prev, slot_capacity: parseInt(e.target.value) }))}
                className="bg-gray-900 border-gray-700 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Tidsintervall (minutter)</Label>
              <select
                value={settings.slot_duration}
                onChange={(e) => setSettings(prev => ({ ...prev, slot_duration: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-600"
              >
                <option value={15}>15 minutter</option>
                <option value={30}>30 minutter</option>
                <option value={60}>60 minutter</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Bestillingsfrist (timer i forveien)</Label>
              <Input
                type="number"
                min="0"
                max="720"
                value={settings.cutoff_hours}
                onChange={(e) => setSettings(prev => ({ ...prev, cutoff_hours: parseInt(e.target.value) || 0 }))}
                className="bg-gray-900 border-gray-700 text-white"
              />
              <p className="text-xs text-gray-500">
                Kunder må booke minst så mange timer i forveien (f.eks. 72 = 3 døgn). Gjelder kun offentlige bookinger – admin kan booke når som helst.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Arbeidsdager</Label>
              <div className="flex flex-wrap gap-2">
                {weekDays.map(day => (
                  <Badge
                    key={day.id}
                    onClick={() => handleToggleWorkingDay(day.id)}
                    className={`cursor-pointer transition-all ${
                      settings.working_days.includes(day.id)
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                  >
                    {day.name}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-3 pt-2 border-t border-gray-800">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-gray-300">Lunsjpause</Label>
                  <p className="text-xs text-gray-500">Blokkerer bookinger i lunsjpausen + valgt buffer før.</p>
                </div>
                <Switch
                  data-testid="lunch-break-enabled-switch"
                  className="data-[state=unchecked]:bg-gray-600"
                  checked={!!settings.lunch_break_enabled}
                  onCheckedChange={(v) => setSettings(prev => ({ ...prev, lunch_break_enabled: v }))}
                />
              </div>
              {settings.lunch_break_enabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-gray-300 text-sm">Lunsj fra</Label>
                    <Input
                      type="time"
                      data-testid="lunch-start-input"
                      value={settings.lunch_start}
                      onChange={(e) => setSettings(prev => ({ ...prev, lunch_start: e.target.value }))}
                      className="bg-gray-900 border-gray-700 text-white"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-gray-300 text-sm">Lunsj til</Label>
                    <Input
                      type="time"
                      data-testid="lunch-end-input"
                      value={settings.lunch_end}
                      onChange={(e) => setSettings(prev => ({ ...prev, lunch_end: e.target.value }))}
                      className="bg-gray-900 border-gray-700 text-white"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-gray-300 text-sm">Buffer før (min)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="120"
                      data-testid="lunch-buffer-input"
                      value={settings.lunch_buffer_before_minutes}
                      onChange={(e) => setSettings(prev => ({ ...prev, lunch_buffer_before_minutes: parseInt(e.target.value) || 0 }))}
                      className="bg-gray-900 border-gray-700 text-white"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3 pt-2 border-t border-gray-800">
              <Label className="text-gray-300">Daglig e-postoppsummering</Label>
              <div className="flex items-center justify-between rounded-lg bg-gray-900 border border-gray-700 px-4 py-3">
                <div className="pr-4">
                  <p className="text-sm text-white">Ikke send i helger</p>
                  <p className="text-xs text-gray-500">Hopp over dager som ikke er arbeidsdager.</p>
                </div>
                <Switch
                  data-testid="daily-summary-skip-weekends-switch"
                  className="data-[state=unchecked]:bg-gray-600"
                  checked={!!settings.daily_summary_skip_weekends}
                  onCheckedChange={(v) => setSettings(prev => ({ ...prev, daily_summary_skip_weekends: v }))}
                />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-gray-900 border border-gray-700 px-4 py-3">
                <div className="pr-4">
                  <p className="text-sm text-white">Ikke send i ferie-/stengte perioder</p>
                  <p className="text-xs text-gray-500">Hopp over dager som er markert som stengt.</p>
                </div>
                <Switch
                  data-testid="daily-summary-skip-holidays-switch"
                  className="data-[state=unchecked]:bg-gray-600"
                  checked={!!settings.daily_summary_skip_holidays}
                  onCheckedChange={(v) => setSettings(prev => ({ ...prev, daily_summary_skip_holidays: v }))}
                />
              </div>
            </div>

            <Button
              onClick={handleSaveSettings}
              disabled={saving}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
            >
              {saving ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Lagrer...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Lagre Innstillinger
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Closed Periods */}
        <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-red-400" />
              <span>Ferie / stengte perioder</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-300">Fra dato</Label>
                  <Input
                    type="date"
                    value={newClosedStart}
                    onChange={(e) => setNewClosedStart(e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white"
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="closed-period-start"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Til dato</Label>
                  <Input
                    type="date"
                    value={newClosedEnd}
                    onChange={(e) => setNewClosedEnd(e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white"
                    min={newClosedStart || new Date().toISOString().split('T')[0]}
                    data-testid="closed-period-end"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">La «Til dato» stå tom for å stenge én enkelt dag.</p>
              <div className="space-y-2">
                <Label className="text-gray-300">Årsak (valgfritt)</Label>
                <Input
                  value={newClosedReason}
                  onChange={(e) => setNewClosedReason(e.target.value)}
                  className="bg-gray-900 border-gray-700 text-white"
                  placeholder="F.eks. Fellesferie, helligdag..."
                  data-testid="closed-period-reason"
                />
              </div>
              <Button
                onClick={handleAddClosedPeriod}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                data-testid="add-closed-period-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Legg til stengt periode
              </Button>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              <Label className="text-gray-300">Stengte perioder ({closedPeriods.length})</Label>
              {closedPeriods.length === 0 ? (
                <p className="text-gray-500 text-sm">Ingen stengte perioder</p>
              ) : (
                <div className="space-y-2">
                  {closedPeriods.map((p) => (
                    <div
                      key={`${p.start}_${p.end}`}
                      className="flex items-center justify-between bg-gray-900/50 p-3 rounded-lg border border-gray-700"
                      data-testid={`closed-period-${p.start}`}
                    >
                      <div>
                        <div className="text-white font-medium">
                          {p.start === p.end
                            ? formatNo(p.start)
                            : `${formatNo(p.start)} – ${formatNo(p.end)}`}
                          <span className="text-gray-500 font-normal ml-2 text-sm">
                            ({p.days} {p.days === 1 ? 'dag' : 'dager'})
                          </span>
                        </div>
                        {p.reason && (
                          <div className="text-gray-400 text-sm">{p.reason}</div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRemovePeriod(p.start, p.end)}
                        className="border-red-600 text-red-400 hover:bg-red-600/20"
                        data-testid={`remove-closed-period-${p.start}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSlotManagement;
